import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useOrganization } from "@/hooks/useOrganization";
import type { ConversationType } from "@/types/conversations";

// UPPDRAG-8 PR-B+C+D — dual-writes the read-state to BOTH legacy tables
// AND the new chat_conversation_participants.last_read_at watermark
// during the 1-week shadow window. PR-F drops the legacy side.
//
// For the bulk-mark-as-read flow (clicking a row in the unified inbox):
//   - direct      → legacy: direct_messages.read_at; new: participants.last_read_at via get_or_create_direct_conversation RPC
//   - training    → legacy: training_messages.read_at (per-message); new: skipped here (per-message dual-write happens in ParentMessageThread; bulk path doesn't have child context)
//   - group_chat  → legacy: message_read_state upsert; new: participants.last_read_at via get_or_create_training_group_conversation RPC
//   - match_chat / open_match_chat → legacy only (PR-E will add new-schema dual-write when match_chat_messages migrates)

export function useMarkConversationRead() {
  const { user } = useAuth();
  const { organization } = useOrganization();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ type, sourceId }: { type: ConversationType; sourceId: string }) => {
      if (!user) return;
      const now = new Date().toISOString();

      if (type === "direct") {
        // 1. LEGACY: per-message read_at
        await supabase
          .from("direct_messages")
          .update({ read_at: now })
          .eq("sender_id", sourceId)
          .eq("recipient_id", user.id)
          .is("read_at", null);

        // 2. NEW: participants.last_read_at watermark — needs org id
        if (organization?.id) {
          try {
            const { data: convId } = await supabase.rpc(
              "get_or_create_direct_conversation",
              { _other_user_id: sourceId, _organization_id: organization.id },
            );
            if (convId) {
              await supabase
                .from("chat_conversation_participants")
                .update({ last_read_at: now })
                .eq("conversation_id", convId as unknown as string)
                .eq("user_id", user.id);
            }
          } catch (err) {
            console.warn("[useMarkConversationRead] direct dual-write failed:", err);
          }
        }
      } else if (type === "training") {
        // Per-message read_at on legacy table (no new-schema dual-write here;
        // ParentMessageThread.tsx handles per-thread dual-write where it has
        // the parent/coach/child IDs needed to resolve the conversation).
        await supabase
          .from("training_messages" as any)
          .update({ read_at: now })
          .eq("recipient_id", user.id)
          .eq("group_id", sourceId)
          .is("read_at", null);
      } else if (type === "group_chat") {
        // 1. LEGACY: message_read_state upsert
        await supabase.from("message_read_state" as any).upsert(
          {
            user_id: user.id,
            conversation_type: type,
            source_id: sourceId,
            last_read_at: now,
          },
          { onConflict: "user_id,conversation_type,source_id" },
        );

        // 2. NEW: participants.last_read_at on the training_group conversation
        try {
          const { data: convId } = await supabase.rpc(
            "get_or_create_training_group_conversation",
            { _group_id: sourceId },
          );
          if (convId) {
            await supabase
              .from("chat_conversation_participants")
              .update({ last_read_at: now })
              .eq("conversation_id", convId as unknown as string)
              .eq("user_id", user.id);
          }
        } catch (err) {
          console.warn("[useMarkConversationRead] group dual-write failed:", err);
        }
      } else {
        // match_chat / open_match_chat: legacy only — PR-E adds new-schema
        // dual-write when match_chat_messages migrates into chat_messages.
        await supabase.from("message_read_state" as any).upsert(
          {
            user_id: user.id,
            conversation_type: type,
            source_id: sourceId,
            last_read_at: now,
          },
          { onConflict: "user_id,conversation_type,source_id" },
        );
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["unified-dm"] });
      qc.invalidateQueries({ queryKey: ["unified-group"] });
      qc.invalidateQueries({ queryKey: ["unified-openmatch"] });
      qc.invalidateQueries({ queryKey: ["unified-training"] });
      qc.invalidateQueries({ queryKey: ["total-unread"] });
    },
  });
}
